import { ConnectorFormDetails, Input, Label } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import type { ConnectorFormProps } from "./GenericVarsForm";

function NumberField(props: {
  id: string;
  label: string;
  value: number | "";
  onChange: (val: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex-1">
      <Label htmlFor={props.id} className="form-label">
        {props.label}
      </Label>
      <Input
        id={props.id}
        type="number"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="font-mono"
      />
    </div>
  );
}

function DatasetFields(props: {
  bronzeDataset: string;
  silverDataset: string;
  connectorPrefix: string;
  updateField: (key: string, value: string) => void;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-1">
        <Label htmlFor="dl-bronze-dataset" className="form-label">
          Bronze Dataset
        </Label>
        <Input
          id="dl-bronze-dataset"
          type="text"
          value={props.bronzeDataset}
          onChange={(e) => props.updateField("bronzeDataset", e.target.value)}
          placeholder={`Default: bronze__${props.connectorPrefix}`}
          className="font-mono"
        />
      </div>
      <div className="flex-1">
        <Label htmlFor="dl-silver-dataset" className="form-label">
          Silver Dataset
        </Label>
        <Input
          id="dl-silver-dataset"
          type="text"
          value={props.silverDataset}
          onChange={(e) => props.updateField("silverDataset", e.target.value)}
          placeholder={`Default: silver__${props.connectorPrefix}`}
          className="font-mono"
        />
      </div>
    </div>
  );
}

export function DeansListForm(props: ConnectorFormProps) {
  const tables = Array.isArray(props.vars.tables) ? (props.vars.tables as TableConfig[]) : [];
  const schedules = (
    props.vars.schedules != null &&
    typeof props.vars.schedules === "object" &&
    !Array.isArray(props.vars.schedules)
      ? props.vars.schedules
      : {}
  ) as Schedules;

  const domain = (props.vars.domain as string) ?? "";
  const lookbackDays = typeof props.vars.lookbackDays === "number" ? props.vars.lookbackDays : "";
  const maxDaysPerRequest =
    typeof props.vars.maxDaysPerRequest === "number" ? props.vars.maxDaysPerRequest : "";
  const bronzeDataset = (props.vars.bronzeDataset as string) ?? "";
  const silverDataset = (props.vars.silverDataset as string) ?? "";

  function updateField(key: string, value: string | number) {
    const next = { ...props.vars };
    if (value === "" || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.onVarsChange(next);
    props.clearBanner();
  }

  function updateNumber(key: string, raw: string) {
    updateField(key, raw ? Number(raw) : "");
  }

  return (
    <>
      <div>
        <Label htmlFor="dl-domain" className="form-label">
          Domain
        </Label>
        <Input
          id="dl-domain"
          type="text"
          value={domain}
          onChange={(e) => updateField("domain", e.target.value)}
          placeholder="Required — e.g. myschool.deanslistsoftware.com"
          className="font-mono"
        />
      </div>
      <TablesEditor
        tables={tables}
        schedules={schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      {!props.hideAdvanced && (
        <ConnectorFormDetails
          summary="Advanced Options"
          className="mt-4 rounded border border-border/50"
        >
          <div className="flex gap-4">
            <NumberField
              id="dl-lookback-days"
              label="Lookback Days"
              value={lookbackDays}
              onChange={(v) => updateNumber("lookbackDays", v)}
              placeholder="Default: 30"
            />
            <NumberField
              id="dl-max-days"
              label="Max Days Per Request"
              value={maxDaysPerRequest}
              onChange={(v) => updateNumber("maxDaysPerRequest", v)}
              placeholder="Default: 1"
            />
          </div>
          <DatasetFields
            bronzeDataset={bronzeDataset}
            silverDataset={silverDataset}
            connectorPrefix={props.connectorPrefix}
            updateField={updateField}
          />
        </ConnectorFormDetails>
      )}
    </>
  );
}
